import React, { useState, useEffect } from "react";

import { FaBars, FaTimes } from "react-icons/fa";

import { scrollToSection } from "./LandingPage";
import { trackHeaderJoinClick, trackNavClick } from "../utils/analytics";

interface HeaderProps {
  isViewingHeroSection: boolean;
  isViewingSignupForm: boolean;
}

interface NavItem {
  id: string;
  label: string;
}

const navItems: NavItem[] = [
  { id: "hero", label: "Home" },
  { id: "why-cohop", label: "Why Cohop" },
  { id: "testimonials", label: "Testimonials" },
  { id: "faq", label: "FAQ" },
  { id: "about-me", label: "About" },
];

const Header: React.FC<HeaderProps> = ({
  isViewingHeroSection,
  isViewingSignupForm,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  const toggleMobileMenu = (): void => {
    if (isMobileMenuOpen) {
      // Start closing animation
      setIsAnimating(true);
      setTimeout(() => {
        setIsMobileMenuOpen(false);
        setIsAnimating(false);
      }, 300); // Match animation duration
    } else {
      // Open menu
      setIsMobileMenuOpen(true);
    }
  };

  const closeMobileMenu = (): void => {
    if (isMobileMenuOpen) {
      setIsAnimating(true);
      setTimeout(() => {
        setIsMobileMenuOpen(false);
        setIsAnimating(false);
      }, 300); // Match animation duration
    }
  };

  const handleNavClick = (sectionId: string): void => {
    trackNavClick(sectionId);
    closeMobileMenu();
    scrollToSection(sectionId);
  };

  // Close mobile menu when clicking outside or on escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener("keydown", handleEscapeKey);
      // Prevent body scroll when mobile menu is open
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isViewingHeroSection
          ? "bg-gradient-to-b from-black/70 via-black/50 to-transparent shadow-none border-none backdrop-blur-sm"
          : "bg-white/90 backdrop-blur-lg shadow-lg border-b border-pink-200"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex justify-between items-center">
        {/* Logo */}
        <div className="flex items-center">
          <img
            src="/logo.svg"
            alt="Cohop Logo"
            className={`h-16 w-auto transition-all duration-300 cursor-pointer ${
              isViewingHeroSection ? "brightness-0 invert" : ""
            }`}
            onClick={() => handleNavClick("hero")}
          />
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-6">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              className={`text-sm font-medium transition-colors duration-200 hover:scale-105 transform ${
                isViewingHeroSection
                  ? "text-white/90 hover:text-white"
                  : "text-gray-700 hover:text-purple-600"
              }`}
            >
              {item.label}
            </button>
          ))}

          {/* CTA Button */}
          {isViewingHeroSection || isViewingSignupForm ? (
            <div className="opacity-0 pointer-events-none">
              <div className="bg-gradient-to-r from-pink-400 to-purple-500 text-white px-4 py-2 rounded-full">
                Sign Up
              </div>
            </div>
          ) : (
            <a
              href="#signup-form"
              onClick={trackHeaderJoinClick}
              className="bg-gradient-to-r from-pink-400 to-purple-500 text-white px-4 py-2 rounded-full hover:from-pink-500 hover:to-purple-600 transition-all duration-300 flex items-center gap-2 font-semibold transform hover:scale-105 shadow-md"
            >
              <span>Request Exclusive Access</span>
            </a>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMobileMenu}
          className={`md:hidden p-2 rounded-lg transition-all duration-200 transform hover:scale-105 ${
            isViewingHeroSection
              ? "text-white hover:bg-white/20"
              : "text-gray-700 hover:bg-gray-100"
          }`}
          aria-label="Toggle mobile menu"
        >
          <div className="relative w-6 h-6 flex items-center justify-center">
            <FaTimes
              className={`text-xl absolute transition-all duration-300 transform ${
                isMobileMenuOpen
                  ? "rotate-0 opacity-100 scale-100"
                  : "rotate-90 opacity-0 scale-75"
              }`}
            />
            <FaBars
              className={`text-xl absolute transition-all duration-300 transform ${
                isMobileMenuOpen
                  ? "-rotate-90 opacity-0 scale-75"
                  : "rotate-0 opacity-100 scale-100"
              }`}
            />
          </div>
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {(isMobileMenuOpen || isAnimating) && (
        <div
          className={`md:hidden fixed inset-0 top-[72px] z-40 transition-all duration-300 ease-in-out ${
            isMobileMenuOpen && !isAnimating
              ? "bg-black/50 backdrop-blur-sm"
              : "bg-black/0 backdrop-blur-none"
          }`}
        >
          {/* Backdrop - click to close */}
          <div
            className="absolute inset-0"
            onClick={closeMobileMenu}
            aria-label="Close menu"
          />

          {/* Menu Content */}
          <div
            className={`relative bg-white border-b border-pink-200 shadow-lg transition-all duration-300 ease-in-out transform ${
              isMobileMenuOpen && !isAnimating
                ? "translate-y-0 opacity-100"
                : "-translate-y-full opacity-0"
            }`}
          >
            <nav className="py-4">
              {navItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNavClick(item.id);
                  }}
                  className={`block w-full text-left px-6 py-3 text-gray-700 hover:bg-pink-50 hover:text-purple-600 transition-all duration-200 font-medium border-b border-gray-100 last:border-b-0 transform ${
                    isMobileMenuOpen && !isAnimating
                      ? "translate-x-0 opacity-100"
                      : "-translate-x-4 opacity-0"
                  }`}
                  style={{
                    transitionDelay:
                      isMobileMenuOpen && !isAnimating
                        ? `${index * 50}ms`
                        : "0ms",
                  }}
                >
                  {item.label}
                </button>
              ))}

              {/* Mobile CTA Button */}
              <div
                className={`px-6 pt-4 pb-2 transition-all duration-300 transform ${
                  isMobileMenuOpen && !isAnimating
                    ? "translate-y-0 opacity-100"
                    : "translate-y-4 opacity-0"
                }`}
                style={{
                  transitionDelay:
                    isMobileMenuOpen && !isAnimating
                      ? `${navItems.length * 50}ms`
                      : "0ms",
                }}
              >
                <button
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    trackHeaderJoinClick();
                    handleNavClick("signup-form");
                  }}
                  className="bg-gradient-to-r from-pink-400 to-purple-500 text-white px-6 py-3 rounded-full hover:from-pink-500 hover:to-purple-600 transition-all duration-300 flex items-center justify-center gap-3 font-semibold transform hover:scale-105 shadow-md w-full"
                >
                  Request Exclusive Access
                </button>
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
